import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SafetyShell from "../safety/SafetyShell";
import { pushRecent } from "../safety/recents";
import {
  Package,
  ChevronDown,
  ChevronRight,
  FileText,
  Users,
  ArrowUpRight,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Activity,
  Upload,
  Send,
  XCircle,
  QrCode,
  Plus,
  History,
  Pencil,
  Trash2,
  Download,
  RefreshCw,
} from "lucide-react";
import CopyDeepLinkButton from "../safety/CopyDeepLinkButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const STATUS_CLS = {
  pending: "sf-badge sf-badge-pending",
  partial: "sf-badge sf-badge-pending",
  complete: "sf-badge sf-badge-approved",
  draft: "sf-badge sf-badge-draft",
  pending_approval: "sf-badge sf-badge-pending",
  approved: "sf-badge sf-badge-approved",
  rejected: "sf-badge sf-badge-rejected",
  not_started: "sf-badge sf-badge-pending",
  in_progress: "sf-badge sf-badge-pending",
  over: "sf-badge sf-badge-rejected",
};
const StatusBadge = ({ status }) => (
  <span className={STATUS_CLS[status] || "sf-badge sf-badge-draft"}>{(status || "—").replaceAll("_", " ")}</span>
);

export default function PODetail({ user, onLogout }) {
  const { poId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return t === "reports" || t === "activity" || t === "changelog" || t === "versions" ? t : "line-items";
  })();
  const [tab, setTabState] = useState(initialTab); // line-items | reports | activity
  const setTab = (next) => {
    setTabState(next);
    const sp = new URLSearchParams(searchParams);
    if (next === "line-items") sp.delete("tab");
    else sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  };
  const [expandedRows, setExpandedRows] = useState({}); // { product_code: { loading, reports } }
  const [activity, setActivity] = useState({ loading: false, events: null });
  const [changelog, setChangelog] = useState({ loading: false, entries: null });
  const [versions, setVersions] = useState({ loading: false, data: null });
  const [permissions, setPermissions] = useState([]);
  const canReplace = permissions.includes("po.replace");

  useEffect(() => {
    axios.get(`${API}/auth/me/permissions`, auth())
      .then((r) => setPermissions(r.data.effective_permissions || []))
      .catch(() => setPermissions([]));
  }, []);

  const reloadVersions = async () => {
    setVersions({ loading: false, data: null });
    await fetchProgress();
  };

  // Auto-expand a line item if ?line=CODE is in URL, after progress loads
  const initialLineParam = searchParams.get("line") || "";
  useEffect(() => {
    if (!initialLineParam || !progress) return;
    if (expandedRows[initialLineParam]) return;
    const exists = (progress.products || []).some((p) => p.product_code === initialLineParam);
    if (exists) toggleRow(initialLineParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, initialLineParam]);

  // Fetch activity feed lazily when Activity tab is selected
  useEffect(() => {
    if (tab !== "activity" || activity.events !== null) return;
    setActivity({ loading: true, events: null });
    axios.get(`${API}/po/${poId}/activity`, auth())
      .then((r) => setActivity({ loading: false, events: r.data.events || [] }))
      .catch(() => setActivity({ loading: false, events: [] }));
  }, [tab, activity.events, poId]);

  // Fetch line-item change log lazily when Change Log tab is selected
  useEffect(() => {
    if (tab !== "changelog" || changelog.entries !== null) return;
    setChangelog({ loading: true, entries: null });
    axios.get(`${API}/po/${poId}/line-item-history`, auth())
      .then((r) => setChangelog({ loading: false, entries: r.data.entries || [] }))
      .catch(() => setChangelog({ loading: false, entries: [] }));
  }, [tab, changelog.entries, poId]);

  // Fetch replacement/amendment history lazily when Versions tab is selected
  useEffect(() => {
    if (tab !== "versions" || versions.data !== null) return;
    setVersions({ loading: true, data: null });
    axios.get(`${API}/po/${poId}/replacement-history`, auth())
      .then((r) => setVersions({ loading: false, data: r.data }))
      .catch(() => setVersions({ loading: false, data: { entries: [] } }));
  }, [tab, versions.data, poId]);

  useEffect(() => {
    fetchProgress();
    // eslint-disable-next-line
  }, [poId]);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/po/${poId}/production-progress`, auth());
      setProgress(res.data);
      // Track in recents
      pushRecent({
        type: "po",
        id: poId,
        label: `PO ${res.data.po_number}`,
        sublabel: res.data.customer_name || "",
        path: `/safety/po/${poId}/detail`,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load PO progress");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = async (productCode) => {
    const open = expandedRows[productCode];
    if (open) {
      setExpandedRows((prev) => {
        const next = { ...prev };
        delete next[productCode];
        return next;
      });
      const sp = new URLSearchParams(searchParams);
      if (sp.get("line") === productCode) {
        sp.delete("line");
        setSearchParams(sp, { replace: true });
      }
      return;
    }
    setExpandedRows((prev) => ({ ...prev, [productCode]: { loading: true, reports: [] } }));
    // Reflect in URL so deep-link includes the expanded line
    const sp = new URLSearchParams(searchParams);
    sp.set("line", productCode);
    setSearchParams(sp, { replace: true });
    try {
      const res = await axios.get(
        `${API}/po/${poId}/line-item-reports/${encodeURIComponent(productCode)}`,
        auth()
      );
      setExpandedRows((prev) => ({
        ...prev,
        [productCode]: { loading: false, reports: res.data.reports || [] },
      }));
    } catch (e) {
      toast.error("Failed to load reports for this line item");
      setExpandedRows((prev) => {
        const next = { ...prev };
        delete next[productCode];
        return next;
      });
    }
  };

  const breadcrumb = useMemo(
    () => [
      { label: "Safety", to: "/safety" },
      { label: "Purchase Orders", to: "/safety/po" },
      { label: progress ? `PO ${progress.po_number}` : "…" },
    ],
    [progress]
  );

  const miniMap = useMemo(
    () => [
      { label: "Safety", to: "/safety", depth: 0 },
      { label: "Purchase Orders", to: "/safety/po", depth: 1 },
      { label: progress ? `PO ${progress.po_number}` : "PO …", active: true, depth: 2 },
    ],
    [progress]
  );

  const rightRail = (
    <div className="space-y-4">
      <div className="sf-right-rail" data-testid="po-detail-rail-overview">
        <h4>Overview</h4>
        {progress && (
          <div className="space-y-2 text-xs">
            <KV label="Customer">
              <button
                onClick={() => navigate(`/safety/customers`)}
                className="sf-link-lateral"
                data-testid="po-detail-customer-link"
              >
                {progress.customer_name || "—"}
              </button>
            </KV>
            <KV label="PO Number"><span className="sf-mono">{progress.po_number}</span></KV>
            <KV label="Lines">
              {progress.overall?.line_complete ?? 0} / {progress.overall?.line_total ?? 0} complete
            </KV>
            <KV label="Total Order"><span className="sf-mono">{progress.overall?.ordered_total ?? 0}</span></KV>
            <KV label="Accepted"><span className="sf-mono">{progress.overall?.accepted_total ?? 0}</span></KV>
            <KV label="Pending">
              <span className="sf-mono" style={{ color: "var(--sf-accent)" }}>
                {progress.overall?.pending_total ?? 0}
              </span>
            </KV>
            <KV label="Progress">
              <span className="sf-mono">{progress.overall?.percent ?? 0}%</span>
            </KV>
          </div>
        )}
      </div>

      <div className="sf-right-rail" data-testid="po-detail-rail-reports">
        <div className="flex items-center justify-between mb-1">
          <h4 className="flex items-center gap-1.5"><FileText className="w-3 h-3" /> Linked Reports</h4>
          {progress?.reports?.length > 0 && (
            <span className="text-[10px] text-zinc-400">{progress.reports.length}</span>
          )}
        </div>
        {!progress?.reports?.length ? (
          <p className="text-xs text-zinc-400 py-2">No reports yet.</p>
        ) : (
          <ul className="space-y-1 mt-2">
            {progress.reports.slice(0, 8).map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => {
                    pushRecent({
                      type: "report", id: r.id, label: `Report ${r.report_date}`,
                      sublabel: `PO ${progress.po_number}`,
                      path: `/safety/production-reports/${r.id}`,
                    });
                    navigate(`/safety/production-reports/${r.id}`);
                  }}
                  className="w-full flex items-start gap-2 px-2 py-1.5 hover:bg-zinc-50 text-left"
                  data-testid={`rail-linked-report-${r.id}`}
                >
                  <ArrowUpRight className="w-3 h-3 mt-1 text-zinc-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs sf-mono">{r.report_date}</span>
                    <span className="block text-[10px] text-zinc-500 truncate">
                      <StatusBadge status={r.status} />
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <SafetyShell user={user} onLogout={onLogout} breadcrumb={breadcrumb} miniMap={miniMap} rightRail={rightRail}>
      {loading ? (
        <div className="sf-surface p-6 flex items-center gap-3 text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading PO progress…
        </div>
      ) : !progress ? (
        <div className="sf-surface p-6 text-zinc-500">PO not found.</div>
      ) : (
        <div className="space-y-4">
          {/* HEADER */}
          <div className="sf-surface p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl tracking-tight font-bold">
                  PO <span className="sf-mono">{progress.po_number}</span>
                </h1>
                <div className="text-sm text-zinc-500 mt-1">
                  {progress.customer_name || "—"}
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="sf-label">Progress</span>
                <span className="sf-mono text-2xl font-semibold">
                  {progress.overall?.percent ?? 0}<span className="text-zinc-400 text-base">%</span>
                </span>
                {progress.overall?.is_over && (
                  <span className="sf-badge sf-badge-rejected"><AlertTriangle className="w-3 h-3" /> over</span>
                )}
                {progress.overall?.line_complete === progress.overall?.line_total && (
                  <span className="sf-badge sf-badge-approved"><CheckCircle2 className="w-3 h-3" /> complete</span>
                )}
                <CopyDeepLinkButton size="sm" testId="po-detail-copy-link" />
              </div>
            </div>
          </div>

          {/* TABS */}
          <div className="sf-surface">
            <div className="flex border-b border-zinc-200">
              <TabButton active={tab === "line-items"} onClick={() => setTab("line-items")} testId="tab-line-items">
                <Package className="w-4 h-4 mr-1.5" /> Line Items <span className="text-zinc-400 ml-1">({progress.products?.length || 0})</span>
              </TabButton>
              <TabButton active={tab === "reports"} onClick={() => setTab("reports")} testId="tab-reports">
                <FileText className="w-4 h-4 mr-1.5" /> Production Reports <span className="text-zinc-400 ml-1">({progress.reports?.length || 0})</span>
              </TabButton>
              <TabButton active={tab === "activity"} onClick={() => setTab("activity")} testId="tab-activity">
                <Activity className="w-4 h-4 mr-1.5" /> Activity
              </TabButton>
              <TabButton active={tab === "changelog"} onClick={() => setTab("changelog")} testId="tab-changelog">
                <History className="w-4 h-4 mr-1.5" /> Change Log
              </TabButton>
              <TabButton active={tab === "versions"} onClick={() => setTab("versions")} testId="tab-versions">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Versions
                {versions.data?.entries?.length > 0 && (
                  <span className="text-zinc-400 ml-1">({versions.data.entries.length})</span>
                )}
              </TabButton>
            </div>

            {tab === "line-items" && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50">
                    <tr className="text-left text-zinc-500">
                      <th className="px-3 py-2 sf-label w-8"></th>
                      <th className="px-3 py-2 sf-label">Code</th>
                      <th className="px-3 py-2 sf-label">Description</th>
                      <th className="px-3 py-2 sf-label text-right">Order</th>
                      <th className="px-3 py-2 sf-label text-right">Accepted</th>
                      <th className="px-3 py-2 sf-label text-right">Pending</th>
                      <th className="px-3 py-2 sf-label text-right">%</th>
                      <th className="px-3 py-2 sf-label">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {progress.products?.length === 0 && (
                      <tr><td colSpan="8" className="px-3 py-6 text-center text-zinc-400">No line items.</td></tr>
                    )}
                    {progress.products?.map((p) => {
                      const open = !!expandedRows[p.product_code];
                      return (
                        <ItemRow
                          key={p.product_code}
                          item={p}
                          open={open}
                          onToggle={() => toggleRow(p.product_code)}
                          rowState={expandedRows[p.product_code]}
                          onOpenReport={(r) => {
                            pushRecent({
                              type: "report", id: r.report_id, label: `Report ${r.report_date}`,
                              sublabel: `PO ${progress.po_number} · ${p.product_code}`,
                              path: `/safety/production-reports/${r.report_id}`,
                            });
                            navigate(`/safety/production-reports/${r.report_id}`);
                          }}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "reports" && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50">
                    <tr className="text-left text-zinc-500">
                      <th className="px-3 py-2 sf-label">Date</th>
                      <th className="px-3 py-2 sf-label">Batch</th>
                      <th className="px-3 py-2 sf-label">Shade</th>
                      <th className="px-3 py-2 sf-label">Created By</th>
                      <th className="px-3 py-2 sf-label">Status</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {progress.reports?.length === 0 && (
                      <tr><td colSpan="6" className="px-3 py-6 text-center text-zinc-400">No production reports yet.</td></tr>
                    )}
                    {progress.reports?.map((r) => (
                      <tr
                        key={r.id}
                        className="border-t border-zinc-100 sf-row-clickable"
                        onClick={() => {
                          pushRecent({
                            type: "report", id: r.id, label: `Report ${r.report_date}`,
                            sublabel: `PO ${progress.po_number}`,
                            path: `/safety/production-reports/${r.id}`,
                          });
                          navigate(`/safety/production-reports/${r.id}`);
                        }}
                        data-testid={`po-detail-report-row-${r.id}`}
                      >
                        <td className="px-3 py-2.5 sf-mono">{r.report_date}</td>
                        <td className="px-3 py-2.5 sf-mono">{r.batch_no || "—"}</td>
                        <td className="px-3 py-2.5">{r.shade_name || "—"}</td>
                        <td className="px-3 py-2.5">{r.created_by_name || "—"}</td>
                        <td className="px-3 py-2.5"><StatusBadge status={r.status} /></td>
                        <td className="px-3 py-2.5 text-right">
                          <ChevronRight className="w-4 h-4 text-zinc-300 inline" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "activity" && (
              <div className="p-5" data-testid="activity-panel">
                {activity.loading ? (
                  <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading activity…</div>
                ) : !activity.events || activity.events.length === 0 ? (
                  <div className="text-sm text-zinc-400">No activity yet.</div>
                ) : (
                  <ol className="relative border-l border-zinc-200 ml-3 space-y-4">
                    {activity.events.map((e, i) => (
                      <ActivityItem key={i} ev={e} navigate={navigate} pushRecent={pushRecent} idx={i} />
                    ))}
                  </ol>
                )}
              </div>
            )}

            {tab === "changelog" && (
              <div className="p-5" data-testid="changelog-panel">
                <p className="text-xs text-zinc-500 mb-4">
                  History of line items added, edited, or removed on this PO.
                </p>
                {changelog.loading ? (
                  <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading change log…</div>
                ) : !changelog.entries || changelog.entries.length === 0 ? (
                  <div className="text-sm text-zinc-400">No line-item changes recorded yet.</div>
                ) : (
                  <ol className="relative border-l border-zinc-200 ml-3 space-y-4">
                    {changelog.entries.map((c) => (
                      <ChangeLogItem key={c.id} entry={c} />
                    ))}
                  </ol>
                )}
              </div>
            )}

            {tab === "versions" && (
              <div className="p-5" data-testid="versions-panel">
                <p className="text-xs text-zinc-500 mb-4">
                  Every time this PO is replaced with an amended file from the customer, the previous
                  version is archived here. Downstream links (reports, QR, pallets, dispatch) are preserved.
                </p>
                {versions.loading ? (
                  <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading versions…</div>
                ) : !versions.data?.entries || versions.data.entries.length === 0 ? (
                  <div className="text-sm text-zinc-400">No amendments yet — this is the original PO (v1).</div>
                ) : (
                  <ol className="relative border-l border-zinc-200 ml-3 space-y-4">
                    {versions.data.entries.map((v) => (
                      <VersionItem key={v.version} entry={v} poId={poId} canReplace={canReplace} onRestored={reloadVersions} />
                    ))}
                  </ol>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </SafetyShell>
  );
}

const TabButton = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    className={`flex items-center px-4 py-2.5 text-sm font-medium border-b-2 ${
      active
        ? "border-orange-600 text-orange-700"
        : "border-transparent text-zinc-500 hover:text-zinc-900"
    }`}
    data-testid={testId}
  >
    {children}
  </button>
);

const CHANGE_META = {
  add_line_item: { icon: Plus, cls: "text-emerald-600 bg-emerald-50", label: "Added" },
  edit_line_item: { icon: Pencil, cls: "text-amber-600 bg-amber-50", label: "Edited" },
  delete_line_item: { icon: Trash2, cls: "text-red-600 bg-red-50", label: "Removed" },
};

const ChangeLogItem = ({ entry }) => {
  const meta = CHANGE_META[entry.action] || { icon: History, cls: "text-zinc-500 bg-zinc-100", label: entry.action };
  const Icon = meta.icon;
  const when = entry.timestamp ? new Date(entry.timestamp).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "";
  return (
    <li className="ml-4" data-testid={`changelog-entry-${entry.id}`}>
      <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ${meta.cls}`}>
        <Icon className="w-3 h-3" />
      </span>
      <div className="pl-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`sf-badge ${meta.cls}`}>{meta.label}</span>
          <span className="text-sm text-zinc-900">{entry.summary}</span>
        </div>
        <div className="text-xs text-zinc-500 mt-0.5">
          {entry.user_name || entry.user_email || "—"} · {when}
        </div>
        {entry.details?.reason && (
          <div className="text-xs text-zinc-500 mt-0.5 italic">Reason: {entry.details.reason}</div>
        )}
      </div>
    </li>
  );
};

const VersionItem = ({ entry, poId, canReplace, onRestored }) => {
  const [restoring, setRestoring] = useState(false);
  const when = entry.replaced_at ? new Date(entry.replaced_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "";
  const d = entry.diff_summary || {};
  const handleRestore = async () => {
    if (!window.confirm(`Roll this PO back to version ${entry.version}?\n\nThe current line items will be replaced with that version's snapshot. Lines with existing production/dispatch data are retained, and the current state is saved as a new version so this is reversible.`)) return;
    setRestoring(true);
    try {
      const res = await axios.post(
        `${API}/po/${poId}/replacement-history/${entry.version}/restore`,
        { reason: `Rolled back to version ${entry.version}` },
        auth(),
      );
      toast.success(res.data?.message || "PO restored");
      if (onRestored) await onRestored();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to restore version");
    } finally {
      setRestoring(false);
    }
  };
  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API}/po/${poId}/replacement-history/${entry.version}/download?token=${encodeURIComponent(token)}`,
        { ...auth(), responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = entry.prev_file_name || `PO_v${entry.version}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download prior version");
    }
  };
  return (
    <li className="ml-4" data-testid={`version-entry-${entry.version}`}>
      <span className="absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full text-orange-600 bg-orange-50">
        <RefreshCw className="w-3 h-3" />
      </span>
      <div className="pl-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="sf-badge text-orange-700 bg-orange-50">v{entry.version} → v{entry.version + 1}</span>
          <span className="text-sm text-zinc-900">Replaced with an amended file</span>
        </div>
        <div className="flex flex-wrap gap-2 mt-1.5 text-xs">
          <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">+{d.added || 0} added</span>
          <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700">{d.updated || 0} updated</span>
          <span className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-600">{d.unchanged || 0} unchanged</span>
          <span className="px-2 py-0.5 rounded bg-red-100 text-red-700">{(d.dropped_codes?.length || 0) + (d.kept_removed_codes?.length || 0)} removed</span>
        </div>
        {d.kept_removed_codes?.length > 0 && (
          <div className="text-xs text-blue-700 mt-1">Retained (had downstream data): {d.kept_removed_codes.join(", ")}</div>
        )}
        {d.dropped_codes?.length > 0 && (
          <div className="text-xs text-zinc-500 mt-0.5">Dropped: {d.dropped_codes.join(", ")}</div>
        )}
        <div className="text-xs text-zinc-500 mt-1">
          {entry.replaced_by || "—"} · {when} · previous file had {entry.prev_line_count} line(s)
        </div>
        {entry.reason && <div className="text-xs text-zinc-500 mt-0.5 italic">Reason: {entry.reason}</div>}
        {entry.has_prev_file && (
          <button
            onClick={handleDownload}
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-orange-700 hover:text-orange-900 font-medium"
            data-testid={`version-download-${entry.version}`}
          >
            <Download className="w-3.5 h-3.5" /> Download {entry.prev_file_name || `v${entry.version} file`}
          </button>
        )}
        {canReplace && (
          <button
            onClick={handleRestore}
            disabled={restoring}
            className="mt-2 ml-4 inline-flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-900 font-medium disabled:opacity-50"
            data-testid={`version-restore-${entry.version}`}
          >
            {restoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <History className="w-3.5 h-3.5" />}
            Restore this version
          </button>
        )}
      </div>
    </li>
  );
};

const KV = ({ label, children }) => (  <div className="flex justify-between gap-3">
    <span className="text-zinc-500">{label}</span>
    <span className="text-zinc-900 text-right">{children}</span>
  </div>
);

const ItemRow = ({ item, open, onToggle, rowState, onOpenReport }) => {
  return (
    <>
      <tr
        className="border-t border-zinc-100 sf-row-clickable"
        onClick={onToggle}
        data-testid={`po-line-row-${item.product_code}`}
      >
        <td className="px-3 py-2.5 text-zinc-400">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-3 py-2.5 sf-mono">{item.product_code}</td>
        <td className="px-3 py-2.5 truncate max-w-[280px]">{item.description}</td>
        <td className="px-3 py-2.5 text-right sf-mono">{item.order_qty}</td>
        <td className="px-3 py-2.5 text-right sf-mono">{item.accepted_qty}</td>
        <td className="px-3 py-2.5 text-right sf-mono" style={{ color: "var(--sf-accent)" }}>{item.pending_qty}</td>
        <td className="px-3 py-2.5 text-right sf-mono">{item.percent}%</td>
        <td className="px-3 py-2.5"><StatusBadge status={item.status} /></td>
      </tr>
      {open && (
        <tr className="bg-zinc-50/60">
          <td></td>
          <td colSpan="7" className="px-3 py-3" data-testid={`po-line-disclosure-${item.product_code}`}>
            {rowState?.loading ? (
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading reports…
              </div>
            ) : rowState?.reports?.length === 0 ? (
              <div className="text-xs text-zinc-400">No production reports for this line item yet.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-zinc-500">
                  <tr className="text-left">
                    <th className="py-1.5 sf-label">Date</th>
                    <th className="py-1.5 sf-label text-right">Coated</th>
                    <th className="py-1.5 sf-label text-right">Rework</th>
                    <th className="py-1.5 sf-label text-right">Accepted</th>
                    <th className="py-1.5 sf-label">Batch</th>
                    <th className="py-1.5 sf-label">By</th>
                    <th className="py-1.5 sf-label">Status</th>
                    <th className="py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {rowState?.reports?.map((r) => (
                    <tr
                      key={r.report_id}
                      className="border-t border-zinc-200 sf-row-clickable"
                      onClick={() => onOpenReport(r)}
                      data-testid={`po-line-report-${r.report_id}`}
                    >
                      <td className="py-1.5 sf-mono">{r.report_date}</td>
                      <td className="py-1.5 text-right sf-mono">{r.coated_qty}</td>
                      <td className="py-1.5 text-right sf-mono">{r.rework_qty}</td>
                      <td className="py-1.5 text-right sf-mono font-semibold">{r.accepted_qty}</td>
                      <td className="py-1.5 sf-mono">{r.batch_no || "—"}</td>
                      <td className="py-1.5">{r.created_by_name || "—"}</td>
                      <td className="py-1.5"><StatusBadge status={r.status} /></td>
                      <td className="py-1.5 text-right">
                        <span className="sf-link-lateral inline-flex items-center gap-0.5">
                          <ArrowUpRight className="w-3 h-3" /> Open
                        </span>
                      </td>
                    </tr>
                  ))}
                  {rowState?.reports?.length > 0 && (
                    <tr className="border-t-2 border-zinc-300" data-testid={`po-line-totals-${item.product_code}`}>
                      <td colSpan="3" className="py-1.5 text-right text-zinc-500">Total accepted</td>
                      <td className="py-1.5 text-right sf-mono font-semibold">
                        {rowState.reports.reduce((s, r) => s + (r.accepted_qty || 0), 0)}
                      </td>
                      <td colSpan="4"></td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
};


const EVENT_META = {
  po_uploaded:        { Icon: Upload,      tone: "text-zinc-700"   },
  report_created:     { Icon: Plus,        tone: "text-blue-600"   },
  report_submitted:   { Icon: Send,        tone: "text-amber-600"  },
  report_approved:    { Icon: CheckCircle2,tone: "text-green-600"  },
  report_rejected:    { Icon: XCircle,     tone: "text-red-600"    },
  qr_batch_generated: { Icon: QrCode,      tone: "text-zinc-700"   },
};

function fmtTimestamp(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  } catch { return ts; }
}

const ActivityItem = ({ ev, navigate, pushRecent, idx }) => {
  const meta = EVENT_META[ev.type] || { Icon: Activity, tone: "text-zinc-500" };
  const Icon = meta.Icon;
  return (
    <li className="ml-4" data-testid={`activity-item-${idx}`}>
      <span className={`absolute -left-2 flex items-center justify-center w-4 h-4 bg-white border border-zinc-200 rounded-full ${meta.tone}`}>
        <Icon className="w-2.5 h-2.5" />
      </span>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-sm font-medium text-zinc-900">{ev.title}</span>
        {ev.link && (
          <button
            onClick={() => {
              if (ev.link.to.includes("/production-reports/")) {
                const id = ev.link.to.split("/").pop();
                pushRecent({ type: "report", id, label: ev.link.label, sublabel: "", path: ev.link.to });
              }
              navigate(ev.link.to);
            }}
            className="sf-link-lateral inline-flex items-center gap-1 text-xs"
            data-testid={`activity-link-${idx}`}
          >
            <ArrowUpRight className="w-3 h-3" /> {ev.link.label}
          </button>
        )}
      </div>
      <div className="text-xs text-zinc-500 mt-0.5">
        <span className="sf-mono">{fmtTimestamp(ev.timestamp)}</span>
        {ev.actor && <> · {ev.actor}</>}
      </div>
      {ev.subtitle && (
        <div className="text-xs text-zinc-600 mt-0.5">{ev.subtitle}</div>
      )}
    </li>
  );
};
